import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import ContentRail from "@/components/ContentRail";
import { Play, Info, Check, Lock } from "lucide-react";
import { startGoogleSignIn } from "@/lib/googleAuth";
import PaidUnlockButton from "@/components/PaidUnlockButton";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/b3b3d666-dd53-4588-8cd3-74d3e1efeb55/images/016154da241c8a68e291bb4fbd070e6f6dbf97b827518ddf243173d6550feb2c.png";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  // Auto-redirect subscribed users straight into their library
  useEffect(() => {
    if (user?.is_subscribed) {
      navigate("/browse", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/content").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const goSignup = () => startGoogleSignIn();
  const goSubscribe = () => navigate("/subscribe");

  const groups = {
    coming: items.filter((i) => i.coming_soon),
    movie: items.filter((i) => i.type === "movie" && !i.coming_soon),
    show: items.filter((i) => i.type === "show" && !i.coming_soon),
    trailer: items.filter((i) => i.type === "trailer" && !i.coming_soon),
    interview: items.filter((i) => i.type === "interview" && !i.coming_soon),
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative">
      <Header />

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end pb-24 md:pb-32 overflow-hidden">
        <img src={HERO_IMG} alt="Featured" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-fade" />
        <div className="absolute inset-0 hero-fade-side" />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-5">
              <span className="w-8 h-px bg-[#FFD700]" /> Now Streaming
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              {user?.is_subscribed ? (
                <>Welcome back,<br/><span className="text-[#E50914]">press play.</span></>
              ) : (
                <>Stories the world<br/><span className="text-[#E50914]">can't stop watching.</span></>
              )}
            </h1>
            <p className="mt-6 text-base md:text-lg text-gray-300 max-w-xl leading-relaxed">
              {user?.is_subscribed
                ? "Your full library is unlocked — dive into the catalog below."
                : <>Unlimited movies, shows, trailers and exclusive interviews. One simple plan at <span className="text-white font-semibold">$3.99/month</span>. Cancel anytime.</>}
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              {user ? (
                user.is_subscribed ? (
                  <Button
                    data-testid="hero-watch-now"
                    onClick={() => navigate("/browse")}
                    className="rounded-full bg-white text-black hover:bg-gray-200 px-7 py-6 text-base font-semibold"
                  >
                    <Play className="w-4 h-4 mr-2 fill-black" /> Watch now
                  </Button>
                ) : (
                  <Button
                    data-testid="hero-subscribe"
                    onClick={goSubscribe}
                    className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold"
                  >
                    Start subscription — $3.99/mo
                  </Button>
                )
              ) : (
                <Button
                  data-testid="hero-get-started"
                  onClick={goSignup}
                  className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold"
                >
                  Sign up — $3.99/mo
                </Button>
              )}
              <Button
                data-testid="hero-browse-catalog"
                onClick={() => {
                  const el = document.getElementById("catalog");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="rounded-full bg-white/10 backdrop-blur hover:bg-white/15 border-white/20 text-white px-7 py-6 text-base font-semibold"
              >
                <Info className="w-4 h-4 mr-2" /> Browse catalog
              </Button>
            </div>

            {!user?.is_subscribed && (
              <div className="mt-5">
                <PaidUnlockButton variant="compact" />
              </div>
            )}

            <div className="mt-8 flex items-center gap-6 text-sm text-gray-400">
              {user?.is_subscribed ? (
                <>
                  <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]"/>Full library unlocked</span>
                  <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]"/>HD streaming</span>
                  <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]"/>Any device</span>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]"/>HD streaming</span>
                  <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]"/>Any device</span>
                  <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]"/>Cancel anytime</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CATALOG (locked preview) */}
      <section id="catalog" className="relative max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24 space-y-12">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">
              {user?.is_subscribed ? "Your library" : "A taste of what's inside"}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              {user?.is_subscribed ? "Pick up where you left off" : "Featured this week"}
            </h2>
          </div>
          {!user?.is_subscribed && (
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-400">
              <Lock className="w-4 h-4 text-[#FFD700]" /> Subscribe to unlock playback
            </div>
          )}
        </div>

        <ContentRail title="Movies" items={groups.movie} locked={!user?.is_subscribed} />
        <ContentRail title="Shows" items={groups.show} locked={!user?.is_subscribed} />
        <ContentRail title="Coming Soon" items={groups.coming} locked={false} />
        <ContentRail title="Trailers" items={groups.trailer} locked={false} />
        <ContentRail title="Interviews" items={groups.interview} locked={!user?.is_subscribed} />
      </section>

      {/* PRICING — hidden for subscribed users */}
      {!user?.is_subscribed && (
        <section className="relative max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#1E1E24] via-[#141419] to-[#0A0A0E] p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#E50914]/20 blur-[120px]" />
          <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-4">One plan. No fluff.</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
                Everything for<br/><span className="text-[#E50914]">$3.99</span>/month
              </h2>
              <p className="mt-5 text-gray-300 max-w-md">
                Movies, shows, exclusive trailers and behind-the-scenes interviews. Unlocked the moment you subscribe.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-8">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-6xl font-black">$3.99</span>
                <span className="text-gray-400">/month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-gray-200">
                {[
                  "Full library access",
                  "HD streaming, any device",
                  "Weekly new releases",
                  "Cancel anytime",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#E50914]" /> {f}</li>
                ))}
              </ul>
              <Button
                data-testid="pricing-cta"
                onClick={user ? goSubscribe : goSignup}
                className="mt-8 w-full rounded-full bg-[#E50914] hover:bg-[#F40612] text-white py-6 text-base font-semibold"
              >
                {user ? "Start subscription" : "Sign up & subscribe"}
              </Button>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-10 text-center text-sm text-gray-500">
        <div className="max-w-[1600px] mx-auto px-6">
          © {new Date().getFullYear()} YBTV. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
